import React, { useContext } from "react";
import { Panel, Skeleton, Text, Title, PageError } from "components/commons";
import lang from "translations";
import classNames from "classnames";
import { formatNumberToMoneyWithCurrencyParams } from "services/money.service";
import { useMount } from "hooks";
import style from "./booking-payment.module.scss";
import { Divider } from "antd";
import { mixpanel } from "mixpanel";
import { VenueContext } from "contexts";

const BookingPayment = ({ data: bookingDetailsData, loading: loadingBookingDetails, error }) => {
  const { venue } = useContext(VenueContext);

  useMount(() => {
    mixpanel.track(TrackEvent.VisitedPage, {
      Page: lang.bookingSummary,
    });
  });

  const formatPaymentMethodLabel = (method = "") => {
    if (!method) return "-";
    return method
      .replace(/_/g, " ")
      .split(" ")
      .map((w) => w[0].toUpperCase() + w.slice(1))
      .join(" ");
  };

  const formatPaymentStatus = (status = "") => {
    if (!status) return "-";
    return status
      .replace(/_/g, " ")
      .split(" ")
      .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  };

  if (error) {
    return <PageError />;
  }

  if (!bookingDetailsData) return null;

  return (
    <div className="grid grid-cols-12 gap-x-4">
      <Panel className={classNames("col-span-12", style.panelHeight)}>
        <Title className="mb-lg">{lang.paymentInfo}</Title>
        {loadingBookingDetails ? (
          <Skeleton />
        ) : (
          <>
            {bookingDetailsData.paymentInfo ? (
              <>
                <Divider />
                <div className="flex justify-between items-center">
                  <Text size="medium" className="font-bold">
                    {lang.paymentStatus || "Payment status"}
                  </Text>
                  <Text size="medium" className="font-bold">
                    {formatPaymentStatus(bookingDetailsData.paymentInfo.paymentStatus)}
                  </Text>
                </div>

                <div className="flex justify-between items-center mt-sm">
                  <Text size="small" className="text-gray-light">
                    {lang.totalPaid || "Total paid"}
                  </Text>
                  <Text size="small" className="font-bold">
                    {formatNumberToMoneyWithCurrencyParams(
                      bookingDetailsData.paymentInfo.paymentTotal,
                      true,
                      bookingDetailsData.paymentInfo.payments?.[0]?.currencyCode ??
                      venue.currencyCode
                    )}
                  </Text>
                </div>

                <Divider />
                {bookingDetailsData.paymentInfo.payments &&
                  bookingDetailsData.paymentInfo.payments.map((p, idx) => (
                    <div className="my-sm flex justify-between" key={idx}>
                      <div>
                        <Text className="font-bold">{formatPaymentMethodLabel(p.paymentMethod)}</Text>
                        <Text className="text-gray-light">
                          {p.currencyCode || venue.currencyCode}
                        </Text>
                      </div>
                      <div className="text-right">
                        <Text className="font-bold">
                          {formatNumberToMoneyWithCurrencyParams(p.subTotal, true, p.currencyCode ?? venue.currencyCode)}
                        </Text>
                        <Text className="text-gray-light">
                          {p.discount ? `${lang.discount || 'Discount'}: -${formatNumberToMoneyWithCurrencyParams(p.discount, true, p.currencyCode ?? venue.currencyCode)}` : null}
                          {p.tax ? <div>{`${lang.tax || 'Tax'}: ${formatNumberToMoneyWithCurrencyParams(p.tax, true, p.currencyCode ?? venue.currencyCode)}`}</div> : null}
                        </Text>
                      </div>
                    </div>
                  ))}
              </>
            ) : (
              <Text className="text-gray-light">-</Text>
            )}
          </>
        )}
      </Panel>
    </div>
  );
};

export default BookingPayment;
